import { FC } from 'react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { Option } from 'components/ui/base/select/Select';
import { VatTreatment, vatTreatmentLabels } from 'store/customers/customer';
import { StyledDeclarationSelect } from 'components/ui/composed/declarations/formSelect/DeclarationSelect.styles';
import {
    ErrorDiv,
    ErrorLabel,
    FieldWrapper,
    FormItem,
    InputDiv,
    InputLabel,
    InputLabelRequired,
    InputSection,
    LabelDiv,
    LabelSpacing,
    MetaSection,
} from 'components/ui/composed/declarations/formInput/DeclarationInput.styles';
import { StyledRef } from 'components/new-ui/base/ref/styles';
import { OptionRow, OptionTitle, TruncatedOptionLabel } from '../styles';
import EllipsisTextTooltip from 'components/EllipsisTextTooltip';

export interface VatTreatmentOption {
    id: VatTreatment;
    title: string;
    label: string;
    referenceCode?: string;
}

interface Props {
    label?: string;
    labelSpacing?: LabelSpacing;
    required?: boolean;
    condensed?: boolean;
    fieldProps: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
}

const VatTreatmentSelect: FC<Props> = ({
    label,
    labelSpacing = 'none',
    required,
    condensed,
    fieldProps,
    fieldMeta,
    fieldHelper,
}) => {
    const id = `select-id-${fieldProps.name}`;

    const options: VatTreatmentOption[] = [
        {
            id: VatTreatment.STANDARD,
            title: vatTreatmentLabels[VatTreatment.STANDARD],
            label: 'Method of payment populated automatically',
            referenceCode: '4.8',
        },
        {
            id: VatTreatment.POSTPONED,
            title: vatTreatmentLabels[VatTreatment.POSTPONED],
            label: 'Code 1A05 - IE Postponed populated automatically',
            referenceCode: '2.3',
        },
    ];

    return (
        <FieldWrapper $viewOnly={false} $template={false}>
            {label && (
                <MetaSection>
                    <LabelDiv condensed={condensed} $labelSpacing={labelSpacing}>
                        <InputLabel htmlFor={id}>
                            {required && <InputLabelRequired right>*</InputLabelRequired>}
                            {label}
                        </InputLabel>
                    </LabelDiv>
                </MetaSection>
            )}

            <InputSection>
                <FormItem validateStatus={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}>
                    <InputDiv>
                        <StyledDeclarationSelect
                            allowClear
                            id={id}
                            condensed={condensed}
                            lock={false}
                            size={condensed ? 'middle' : 'large'}
                            optionLabelProp="label"
                            placeholder="Select VAT Treatment"
                            value={fieldProps.value || undefined}
                            onChange={(value) => fieldHelper?.setValue(value)}
                            onBlur={() => fieldHelper?.setTouched(true)}
                            status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                        >
                            {options.map((option) => (
                                <Option key={option.id} value={option.id} label={option.title}>
                                    <OptionTitle>{option.title}</OptionTitle>
                                    <OptionRow>
                                        <StyledRef>{option.referenceCode}</StyledRef>
                                        <EllipsisTextTooltip tooltipTitle={`${option.referenceCode} ${option.label}`}>
                                            <TruncatedOptionLabel>{option.label}</TruncatedOptionLabel>
                                        </EllipsisTextTooltip>
                                    </OptionRow>
                                </Option>
                            ))}
                        </StyledDeclarationSelect>
                    </InputDiv>
                    <ErrorDiv error={!!fieldMeta?.error} touched={!!fieldMeta?.touched} condensed={condensed}>
                        {!!fieldMeta?.error && !!fieldMeta?.touched && <ErrorLabel>{fieldMeta?.error}</ErrorLabel>}
                    </ErrorDiv>
                </FormItem>
            </InputSection>
        </FieldWrapper>
    );
};

export default VatTreatmentSelect;
